/**
 * API client for the Omega backend.
 *
 * Uses VITE_API_URL at build time; falls back to relative path (for proxied dev).
 * JWT token is stored after login and sent as Bearer token.
 */

const BASE = (import.meta.env.VITE_API_URL || "/api").replace(/\/$/, "");

/** Read the stored JWT (empty string when none / no storage). */
export function getToken() {
  if (typeof localStorage === "undefined") return "";
  return localStorage.getItem("omega_token") || "";
}

/** Store the JWT token after successful login. */
export function setToken(token) {
  localStorage.setItem("omega_token", token);
}

/** Clear the stored token (logout). */
export function clearToken() {
  localStorage.removeItem("omega_token");
}

/** Check if a token is stored. */
export function hasToken() {
  return !!getToken();
}

function headers() {
  const h = { "Content-Type": "application/json" };
  const t = getToken();
  if (t) h["Authorization"] = `Bearer ${t}`;
  return h;
}

/**
 * The single readable message for ANY response whose body is not the JSON the
 * Console expects: an SPA index.html served because a request lost its `/api`
 * prefix (HTTP 200 text/html), a proxy/gateway error page, or a stale-session
 * redirect. Surfacing this instead of the raw `Unexpected token '<', "<!DOCTYPE"`
 * parse error keeps the failure legible (heuristic 9: error recovery) — no
 * em dashes, names a cause and a next action.
 */
export const NON_JSON_MESSAGE =
  "Cannot reach the Omega API (unexpected non-JSON response). Your session may be stale, so please hard-refresh.";

/**
 * Window event fired when an ALREADY-authenticated call's credential is
 * rejected (session expiry). The app (useAuth) listens for it to clear local
 * auth state and route back to the login screen. A CustomEvent-free plain Event
 * keeps api.js free of React while staying testable via window.dispatchEvent.
 */
export const SESSION_EXPIRED_EVENT = "omega:session-expired";

/**
 * Window event fired when a call has just REVOKED the current session on purpose
 * (F14 FR11/FR12: an authenticated password change increments
 * `users.credential_version`, which invalidates every JWT minted before it,
 * including the one that authorised the change).
 *
 * Deliberately separate from SESSION_EXPIRED_EVENT even though both end in a
 * logout. That one means "your credential stopped working, we do not know why";
 * this one means "you changed your password, so sign in again" — a different
 * sentence for the user, and a state the app KNOWS rather than infers from a
 * 401. Keeping them apart is also what lets useAuth show the honest notice
 * instead of a generic expiry message.
 */
export const PASSWORD_CHANGED_EVENT = "omega:password-changed";

/**
 * Drop the now-dead JWT and announce the deliberate revocation.
 *
 * Lives at the api.js seam rather than in the component that happens to call
 * changePassword, for the same reason maybeSignalSessionExpiry does: the
 * revocation is a property of the REQUEST, not of one caller. A second call
 * site added later inherits the correct behaviour instead of having to
 * re-implement it (and, more likely, forget to).
 */
function signalPasswordChanged() {
  clearToken();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(PASSWORD_CHANGED_EVENT));
  }
}

/**
 * Backend 401 error `code`s that mean an already-authenticated request's
 * credential (JWT bearer via `get_current_user`, or a per-tenant/global key via
 * `APIKeyMiddleware`) was rejected — i.e. the session has expired or been
 * revoked. A bare 401 is NOT sufficient: the login form
 * ("Invalid email or password") and the change-password check
 * ("current password is incorrect") also return 401 but must NOT log the user
 * out, so their codes are deliberately absent from this set.
 *
 * OMEGA-590 — these are the stable machine codes on the NEW error envelope
 * (`{ code, message, request_id, details }`); they replaced the exact-string
 * `detail` matches ("Invalid or expired token" / "Invalid or missing API key")
 * the Console keyed off before the error-envelope hard cutover.
 */
const SESSION_EXPIRED_CODES = new Set([
  "invalid_token", // backend/app/core/dependencies.py get_current_user
  "invalid_api_key", // backend/app/core/middleware.py APIKeyMiddleware
]);

/**
 * Parse a response body as JSON, returning `undefined` ONLY when the body does
 * not actually parse — a text/html index.html from a mis-prefixed path, a
 * gateway error page, an empty body, or malformed JSON. NEVER throws: a raw
 * `Unexpected token '<'` SyntaxError can never escape to the UI.
 *
 * We attempt the parse REGARDLESS of the Content-Type header. A JSON body that
 * a proxy mislabels as text/plain, or a backend problem+json body
 * (application/problem+json), is still genuine JSON and MUST be used — losing
 * its `message`/`code` (and, for a 401, its session-expiry signal) to a header
 * quirk would be a worse bug than the raw parse error. The friendly fallback fires
 * only when parsing genuinely throws, so real HTML like `<!DOCTYPE ...>` (which
 * throws) still yields the readable NON_JSON_MESSAGE.
 */
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return undefined; // HTML / empty / malformed — swallow the raw parse error
  }
}

/**
 * When a 401 on an authenticated call carries a session-expiry error `code`,
 * clear the stored token and emit SESSION_EXPIRED_EVENT so the app logs out and
 * redirects to login. Deliberately narrow (see SESSION_EXPIRED_CODES): login and
 * change-password 401s are excluded, so wrong credentials never trigger a false
 * logout.
 *
 * `requestToken` is the credential captured in scope when the request was
 * ISSUED (see fetchJson/fetchNoContent). A late 401 for an OLD session must not
 * clear a token that has since been re-issued (logout -> re-login -> a stale
 * 401 from the previous session lands): if the captured credential no longer
 * matches the stored one, this response belongs to a superseded session, so we
 * leave the current token intact and emit nothing. When no credential was
 * captured (an unauthenticated call such as login), the guard is a no-op —
 * those 401 codes are never in SESSION_EXPIRED_CODES anyway.
 */
function maybeSignalSessionExpiry(status, body, requestToken) {
  if (status !== 401) return;
  const code = typeof body?.code === "string" ? body.code : "";
  if (!SESSION_EXPIRED_CODES.has(code)) return;
  if (requestToken !== undefined && requestToken !== getToken()) return;
  clearToken();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
}

/**
 * Turn a non-OK response into a readable Error and, for a session-expiry 401 on
 * an authenticated call, trigger auto-logout. A JSON error body flows through
 * readDetail (the NEW envelope's human `message`); a non-JSON error page
 * (proxy/gateway/stale HTML) yields the friendly NON_JSON_MESSAGE instead of a
 * raw parse error. Always throws. `requestToken` is the credential the request
 * carried (see maybeSignalSessionExpiry).
 *
 * OMEGA-590 — the thrown Error carries the envelope's stable fields so ANY
 * caller (not just the coded managed-upload seam) can branch on them:
 * `err.status`, `err.code` (e.g. useAuth keys the L01 verify-your-email branch
 * off `err.code === "email_not_verified"`), `err.request_id` (== the
 * `X-Request-Id` header, for support diagnostics), and `err.details`.
 */
async function throwResponseError(res, requestToken) {
  const body = await safeJson(res);
  if (body === undefined) {
    throw new Error(NON_JSON_MESSAGE);
  }
  maybeSignalSessionExpiry(res.status, body, requestToken);
  throw errorFromEnvelope(res.status, body);
}

/**
 * Build a readable Error from a parsed NEW error envelope (OMEGA-590), attaching
 * the stable diagnostic fields (`status`, `code`, `request_id`, `details`). The
 * `.message` is the envelope's human `message` via readDetail (never
 * "[object Object]"), falling back to `HTTP <status>`.
 */
function errorFromEnvelope(status, body) {
  const err = new Error(readDetail(body) || `HTTP ${status}`);
  err.status = status;
  if (body && typeof body === "object") {
    if (typeof body.code === "string") err.code = body.code;
    if (typeof body.request_id === "string") err.request_id = body.request_id;
    if (body.details !== undefined) err.details = body.details;
  }
  return err;
}

/**
 * The single seam every JSON-returning api.js caller uses. On a 2xx it returns
 * the parsed JSON body; on a non-2xx it throws a readable Error (and, for a
 * session-expiry 401, triggers auto-logout). A non-JSON body — the classic
 * "hit index.html because the path lost its /api prefix" 200 — yields the
 * friendly NON_JSON_MESSAGE, never a raw `Unexpected token '<'`. `requestToken`
 * is the credential the request carried (see maybeSignalSessionExpiry).
 */
async function parseJsonOrThrow(res, requestToken) {
  if (!res.ok) {
    await throwResponseError(res, requestToken);
  }
  const body = await safeJson(res);
  if (body === undefined) {
    throw new Error(NON_JSON_MESSAGE);
  }
  return body;
}

/**
 * Issue a request whose bearer credential is CAPTURED in scope at issue time,
 * then parse the JSON body (throwing readably on a non-2xx). Capturing the
 * token before the fetch is what lets maybeSignalSessionExpiry tell a genuine
 * current-session expiry apart from a delayed 401 for a superseded session — a
 * stale 401 that arrives after the user has re-logged-in must NOT wipe the
 * freshly-issued token.
 */
async function fetchJson(url, init) {
  const requestToken = getToken();
  const res = await fetch(url, init);
  return parseJsonOrThrow(res, requestToken);
}

/**
 * Same credential-capture as fetchJson, for the 204 No Content endpoints
 * (DELETE project / API key, change password): on a 2xx there is no body to
 * parse, so resolve undefined; on a non-2xx throw readably (and, for a genuine
 * current-session expiry, signal auto-logout).
 */
async function fetchNoContent(url, init) {
  const requestToken = getToken();
  const res = await fetch(url, init);
  if (!res.ok) await throwResponseError(res, requestToken);
  return undefined;
}

/**
 * The coded-error seam for the E02.7 managed-upload lifecycle. Same JSON parse +
 * session-expiry handling as fetchJson, but on a non-2xx it throws an Error that
 * ALSO carries the backend's stable coded fields so callers (useManagedUpload)
 * can branch on them. OMEGA-590 — these now read the NEW error envelope
 * (`{ code, message, request_id, details }`): `err.code` (the stable machine
 * code, e.g. "queue_full" / "idempotency_conflict"), `err.field`
 * (`details.field` when present), `err.status`, `err.request_id` (== the
 * `X-Request-Id` response header, for support diagnostics), and `err.details`.
 * The `.message` is still the readable `readDetail(body)` sentence (the
 * envelope's human `message`, never "[object Object]"), and a non-JSON body
 * still yields NON_JSON_MESSAGE.
 *
 * Confirm returns HTTP 200 even on a validation failure (the failure rides in
 * the body), so a caller reads the body on 2xx and only lands here for a genuine
 * non-2xx (409/410/422/429/503).
 */
async function fetchJsonCoded(url, init) {
  const requestToken = getToken();
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await safeJson(res);
    if (body === undefined) throw new Error(NON_JSON_MESSAGE);
    maybeSignalSessionExpiry(res.status, body, requestToken);
    const err = errorFromEnvelope(res.status, body);
    const d = body?.details;
    if (d && typeof d === "object" && !Array.isArray(d) && d.field) {
      err.field = d.field;
    }
    throw err;
  }
  const body = await safeJson(res);
  if (body === undefined) throw new Error(NON_JSON_MESSAGE);
  return body;
}

/**
 * POST /auth/login - authenticate and receive a JWT.
 *
 * Thin transport ONLY: this returns the TokenResponse and deliberately does NOT
 * persist the JWT. Token persistence is the CALLER's responsibility (useAuth.
 * doLogin) so that out-of-order completion of overlapping login attempts cannot
 * cross sessions — only the winning (latest) attempt commits its token via
 * setToken. If api.login stored the token itself, a superseded attempt resolving
 * LAST would overwrite a newer session's credential (tenant/account crossover).
 * The winning-generation guard lives in useAuth (the auth-state owner).
 */
export async function login(identifier, password) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    // OMEGA-814 — the login identity is the customer's email. Sent as `identifier`
    // rather than `email` because that is what the field IS: for an account created
    // before this change there is no stored email, and the customer signs in with the
    // username they have always used. The backend collects every row the value could
    // name and lets the password pick between them, so both reach the right row through
    // this one key. (It does NOT try email first: the two namespaces overlap, and
    // preferring either one locks the other account out.)
    body: JSON.stringify({ identifier, password }),
  });
  return parseJsonOrThrow(res);
}

/**
 * POST /auth/register - create an account (L01: email + magic-link verification).
 *
 * `email` is REQUIRED (L01 FR5): the backend stores it lower-cased/trimmed,
 * emails a verification link, and gates login until it is confirmed. `email` is
 * format-validated client-side before this is called.
 *
 * OMEGA-762 (2026-08-17) — no payment token is sent. This used to forward an
 * opaque Stripe token and the backend used to 422 without one. The platform will
 * not collect payment from a stored card (decision by Rick Zhou; Omega issues its
 * own invoices, OMEGA-734 D1). This named bank transfer as the settlement rail
 * until OMEGA-773; that was rejected by the owner on 2026-08-18. No rail is named
 * here now, because E20 owns that decision and this client does not.
 *
 * OMEGA-772 — this said "`require_payment_on_register` now defaults false, so the
 * 422 is unreachable". That was the OMEGA-762 state and it went stale one commit
 * later: OMEGA-765 **retired the flag outright** and removed `payment_token` from
 * `RegisterRequest`. The 422 is not unreachable-by-default, it does not exist.
 *
 * Handles BOTH 201 response shapes (L01 FR7):
 *   - verification-pending `{ status: "verification_pending", username, email }`
 *     (`username` is the normalized email under OMEGA-814)
 *     when require_email_verification is on (deployed default) — NO JWT, so no
 *     token is stored; the caller shows a "check your inbox" state.
 *   - TokenResponse `{ access_token, token_type, username }` when the flag is
 *     off (local/dev auto-verify) — the JWT is stored exactly as before L01.
 *
 * Errors arrive on the NEW envelope `{ code, message, request_id, details }`
 * (an already-registered-email 409, a 503, and the
 * password-complexity 422). readDetail surfaces the human `message` — never
 * rendered as "[object Object]".
 */
export async function register(fullName, email, password) {
  // OMEGA-814 — `full_name` is the customer's DISPLAY name and replaces the old
  // `username` field. It does not identify the account and is not unique; the email
  // does both. Snake-cased because the wire contract is the backend's, not this
  // module's camelCase convention.
  const body = { full_name: fullName, email, password };
  const res = await fetch(`${BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await parseJsonOrThrow(res);
  // Only the auto-verify (flag-off) shape carries a JWT; the verification-pending
  // shape has none, so do NOT store a token — the account is unusable until the
  // emailed magic link is confirmed via verifyEmail().
  if (data.access_token) setToken(data.access_token);
  return data;
}

/**
 * POST /auth/verify-email - confirm a magic-link token and auto-login (L01 FR8).
 *
 * `token` is the RAW high-entropy token read from the emailed link's
 * `?token=<raw>` query. On success the backend flips `email_verified=true`,
 * consumes the single-use token, and returns a TokenResponse — we store the JWT
 * so the user lands straight in the Console (auto-login on verify). Errors:
 * 400 "invalid_or_expired_token", 410 "token_already_used" (single-use replay).
 */
export async function verifyEmail(token) {
  const res = await fetch(`${BASE}/auth/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  const data = await parseJsonOrThrow(res);
  setToken(data.access_token);
  return data;
}

/**
 * POST /auth/resend-verification - re-issue and re-send the verification email
 * (L01 FR10). ALWAYS resolves 202 with `{ status: "accepted" }` regardless of whether
 * the account exists or awaits verification (no account enumeration), so the caller
 * shows the same neutral confirmation either way. No token is stored (this does not
 * authenticate).
 *
 * OMEGA-814 — keyed on the same `identifier` as login, because its only caller is the
 * login screen's post-403 "Resend verification email" affordance and it forwards
 * whatever is in the login field. That field now holds an email. The always-202
 * contract is precisely what makes a key mismatch here dangerous: it would report
 * success, send nothing, and leave the customer watching an empty inbox.
 */
export async function resendVerification(identifier) {
  const res = await fetch(`${BASE}/auth/resend-verification`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier }),
  });
  return parseJsonOrThrow(res);
}

/**
 * POST /auth/forgot-password - request a password reset link (F14 FR3/FR4).
 *
 * Keyed on `email` (the reset link goes to a mailbox, so the mailbox is the
 * identifier). Under OMEGA-814 that is now the same value login is keyed on, which
 * is why the recovery flow no longer needs its own explanation of why it differs.
 *
 * ALWAYS resolves 202 with the byte-identical `{ status: "accepted" }` for an
 * eligible account, an unknown address, an inactive or unverified account, one
 * inside the per-account cooldown, and a legacy account with no email on file.
 * The caller therefore CANNOT branch on the outcome, and must not try: the
 * single neutral confirmation is the anti-enumeration contract, not a UI
 * convenience. Non-2xx here means transport or throttling (429 / 503), never
 * account state.
 *
 * Sends no Authorization header: this is a logged-OUT flow and a stale bearer
 * would be both useless and an unnecessary credential on the wire.
 */
export async function forgotPassword(email) {
  const res = await fetch(`${BASE}/auth/forgot-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return parseJsonOrThrow(res);
}

/**
 * POST /auth/reset-password - redeem a reset token and set a new password
 * (F14 FR9/FR10).
 *
 * `token` is the RAW token read from the emailed link's `?token=<raw>` query;
 * it is passed through from component state and is never written to
 * localStorage. Returns 204 with NO body, so there is nothing to parse, and
 * deliberately returns NO JWT: a reset does not sign you in (F14 FR10). The
 * caller sends the user back to login to authenticate with the new password.
 *
 * On success the stored JWT is cleared HERE as well as by the caller's session
 * reset. That is not belt-and-braces for its own sake: the reset screen renders
 * outside the auth gate, so a stale token left in localStorage would make
 * `hasToken()` true and let useAuth initialise a "logged in" session on the next
 * mount against a credential the server has just revoked.
 *
 * Errors: 400 "invalid_or_expired_token", 410 "token_already_used" (single-use
 * replay), 422 (password policy, surfaced by readDetail), 429, 503. None of them
 * is a 401, so none can trip the session-expiry auto-logout.
 */
export async function resetPassword(token, newPassword) {
  await fetchNoContent(`${BASE}/auth/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  clearToken();
  return undefined;
}

/**
 * Drop null / empty-string / empty-array fields from a payload so a bare submit
 * stays byte-compatible with the pre-F03 request (the backend treats absent
 * optional workload fields as their defaults). `false`, `0`, and populated
 * objects/arrays are preserved.
 */
function pruneEmpty(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined) continue;
    if (v === "") continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    out[k] = v;
  }
  return out;
}

/**
 * POST /jobs — submit a job through Adam/Eve via the HSA scheduler.
 *
 * Accepts the full F03 workload spec (E01 container_image/command/args/env/
 * secret_refs/gpu/timeout/retries + E02.2 external/manifest `inputs[]`).
 * Null/empty workload fields are pruned so a resource-only submit produces the
 * same byte payload as before F03. secret_refs carry REFERENCES only — never a
 * literal secret value (enforced upstream by jobValidation + useJobForm).
 *
 * E02.7 managed upload: when `spec.input_plan` is set (mode="managed_upload"
 * with a declared file manifest), this creates a `waiting_for_inputs` job + open
 * upload session in one atomic call. That path sends `io_mode:"managed_wrapper"`
 * and REQUIRES an `Idempotency-Key` header (passed as `opts.idempotencyKey`) so
 * a retried create returns the same job/session instead of a duplicate. A
 * synchronous (non-managed) submit sends neither payload field, keeping its body
 * byte-compatible with the pre-E02.7 request.
 *
 * OMEGA-867 — the SYNCHRONOUS submit now also honours `opts.idempotencyKey`, and
 * the caller should always pass one. The header is optional on that path server
 * side, but omitting it means a request the backend commits whose response is
 * lost turns into a SECOND billable job on retry, which no client-side guard can
 * prevent (the client cannot know whether the server committed). The key must be
 * STABLE across retries of one submission intent — see `useSubmitJob`, which owns
 * that lifetime; a key regenerated per attempt closes nothing.
 *
 * Both paths use the coded-error seam so a caller can branch on `err.code`
 * (`idempotency_conflict` in particular). It is a strict superset of the plain
 * seam — same readable `.message` — so the synchronous path loses nothing by it.
 */
export async function submitJob(spec = {}, opts = {}) {
  const {
    service_class,
    compute_required,
    memory_required,
    sovereign_region,
    esg_required,
    container_image,
    command,
    args,
    env,
    secret_refs,
    gpu_type,
    gpu_count,
    timeout_secs,
    max_retries,
    inputs,
    workload_name,
    project_id,
    input_plan,
  } = spec;

  const managed = input_plan && input_plan.mode === "managed_upload";

  const body = pruneEmpty({
    client_id: "omega-ai-hpc-console",
    service_class,
    compute_required,
    memory_required,
    bandwidth_required: 1.0,
    sovereign_region: sovereign_region || null,
    esg_required,
    container_image,
    command,
    args,
    env,
    secret_refs,
    // gpu_type defaults to "none" on the backend; only send a concrete value.
    gpu_type: gpu_type && gpu_type !== "none" ? gpu_type : null,
    gpu_count: gpu_count || null,
    timeout_secs,
    max_retries: max_retries || null,
    inputs,
    // Job identity (Submit UX + Job Identity PRD, FR3). Both fields are REQUIRED
    // since E14.2 / OMEGA-466 — the backend rejects a submission missing either
    // with 422 (jobs.project_id / jobs.workload_name are NOT NULL). pruneEmpty must
    // NOT drop them; the Submit UX enforces both are present before calling this.
    workload_name,
    project_id,
    // E02.7 — only present for a managed-upload create; io_mode must be
    // managed_wrapper for that path (the backend rejects other combinations).
    input_plan: managed ? input_plan : null,
    io_mode: managed ? "managed_wrapper" : null,
  });

  const h = headers();
  if (opts.idempotencyKey) h["Idempotency-Key"] = opts.idempotencyKey;

  // OMEGA-867 — BOTH paths surface the stable coded errors (409 idempotency_conflict,
  // 422 idempotency_key_too_long, 429 quota_exceeded, 503 ...) so the Console can name the
  // cause. The synchronous path used the plain seam, which was fine while it sent no key and
  // so could never receive an idempotency verdict; now that it can, a caller that cannot read
  // `err.code` would have to pattern-match a prose sentence to tell a conflict from a quota
  // rejection. `fetchJsonCoded` is a strict superset (identical readable `.message`, plus the
  // coded fields), so nothing regresses for existing callers.
  return fetchJsonCoded(`${BASE}/jobs`, {
    method: "POST",
    headers: h,
    body: JSON.stringify(body),
  });
}

/**
 * POST /jobs/spec:validate — dry-run validate a declarative YAML job spec
 * (Submit UX PRD FR5). Body is { format, spec }; `format` defaults to "omega"
 * (native) when falsy, else "k8s". Detection only: this writes ZERO rows and
 * dispatches nothing.
 *
 * CRITICAL: this endpoint returns HTTP 200 even when the spec is INVALID — the
 * reasons ride in the body as { valid: false, submission: null, errors: [...] }.
 * So we do NOT throw on `valid: false`; we return the parsed
 * JobSpecPreviewResponse and let the caller branch on `body.valid`. We only
 * throw on a non-2xx (e.g. 503 "Database not available", which a dry-run must
 * surface honestly rather than mask as "invalid spec").
 */
export async function validateJobSpec(format, spec) {
  return fetchJson(`${BASE}/jobs/spec:validate`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ format: format || "omega", spec }),
  });
}

/**
 * POST /jobs/spec — submit a job from a declarative YAML spec (Submit UX PRD
 * FR6). Body is { format, spec }; `format` defaults to "omega" when falsy.
 * Returns the SAME JobResponse shape as submitJob. On a non-2xx (422 for a
 * bad/unmappable spec, an unknown/archived project, or a failed submission
 * constraint) the NEW error envelope's human `message` is thrown via
 * readDetail(err), rendering as a readable sentence, never "[object Object]".
 *
 * OMEGA-867 — takes the same optional `idempotencyKey` as submitJob, because this
 * endpoint delegates to the SAME create and was equally exposed to a lost response
 * becoming a second billable job. The server fingerprints the MAPPED submission
 * rather than the raw YAML, so two byte-different specs that map to the same job
 * replay each other instead of conflicting. Coded-error seam for the same reason
 * as submitJob: a caller must be able to tell a conflict from a bad spec.
 */
export async function submitJobSpec(format, spec, idempotencyKey) {
  const h = headers();
  if (idempotencyKey) h["Idempotency-Key"] = idempotencyKey;
  return fetchJsonCoded(`${BASE}/jobs/spec`, {
    method: "POST",
    headers: h,
    body: JSON.stringify({ format: format || "omega", spec }),
  });
}

/**
 * GET /projects — the authenticated tenant's NON-archived projects, newest first
 * (Submit UX + Job Identity PRD, FR1). Returns { projects: [{ project_id,
 * tenant_id, name, created_at, archived_at }] }. Server-ordered — DO NOT re-sort.
 * The owning tenant is resolved from the JWT, never a query param. Errors: 400
 * without a tenant claim (object detail; readDetail surfaces its message), 503
 * degraded, 401 on a bad token.
 */
export async function listProjects() {
  return fetchJson(`${BASE}/projects`, { headers: headers() });
}

/**
 * POST /projects — create a tenant-scoped project (FR1). Body is ONLY { name };
 * the owning tenant is taken from the JWT, never the body. Returns 201 with the
 * created project. Errors: 409 "a project named '<name>' already exists for this
 * tenant" (object detail; readDetail surfaces its message — names are unique per
 * tenant), 422 (name 1..128), 400 without a tenant claim, 503 degraded.
 */
export async function createProject(name) {
  return fetchJson(`${BASE}/projects`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ name }),
  });
}

/**
 * DELETE /projects/{id} — soft-delete (archive) a project (FR2). Returns 204 with
 * NO body, so we resolve on res.ok WITHOUT calling res.json(). Errors: 404 unknown
 * OR cross-tenant (never confirms another tenant's project exists), 409
 * "project has active (non-terminal) jobs and cannot be deleted" (object detail;
 * readDetail surfaces its message — the UI renders it readably), 400/503.
 */
export async function deleteProject(projectId) {
  // 204 No Content — there is no body to parse.
  return fetchNoContent(`${BASE}/projects/${encodeURIComponent(projectId)}`, {
    method: "DELETE",
    headers: headers(),
  });
}

/**
 * Render the NEW error envelope readably (OMEGA-590). Every backend error is now
 * `{ code, message, request_id, details }` — surface the human `message`, and
 * fall back to the stable machine `code` when a message is somehow absent, so a
 * caller always gets a legible string (never "[object Object]"). Passed the
 * parsed response body; a bare string is returned as-is for resilience.
 */
export function readDetail(err) {
  if (err == null) return "";
  if (typeof err === "string") return err;
  if (typeof err.message === "string" && err.message) return err.message;
  if (typeof err.code === "string" && err.code) return err.code;
  return "";
}

// ── E02.7 Managed Upload lifecycle clients ─────────────────────────────────
// The legacy E02.1 files[]-shaped presign/confirm clients (sha256Hex via
// crypto.subtle, presignInputs, uploadToPresignedUrl PUT, confirmInputs) are
// RETIRED: the backend now 410s that request shape. The canonical flow is
// session/attempt identity (create -> presign -> POST -> confirm -> finalize),
// and hashing is the streaming SHA-256 in engine/sha256.js (crypto.subtle has no
// streaming digest and FR17 forbids whole-file arrayBuffer()).

/**
 * GET /upload-capabilities (E02.7 FR2) — authoritative, cacheable discovery of
 * the managed-upload modes, safe-filename grammar, hash algorithm, limits
 * (max files / per-file / total bytes), session/target TTLs, and transport. The
 * Console consumes these limits instead of hard-coding them; when managed upload
 * is unavailable (dark-launch flag off) `managed_upload_available` is false and
 * `managed_upload` is absent from `modes`, so the Console fails closed. No secrets.
 */
export async function getUploadCapabilities() {
  return fetchJson(`${BASE}/upload-capabilities`, { headers: headers() });
}

/**
 * GET /jobs/{jobId}/inputs (E02.7 FR15) — the tenant-owned session / logical
 * file / current-attempt recovery read model. Never returns a presigned URL,
 * policy, signature, or credential. Used to reconstruct upload state after a
 * refresh: validated files stay validated, a pending attempt can be confirmed,
 * and a file whose local bytes are gone needs re-selection (matching name/size/
 * sha256). A cross-tenant / unknown job is a non-disclosing 404.
 */
export async function getJobInputs(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs`, { headers: headers() });
}

/**
 * POST /jobs/{jobId}/inputs:presign (E02.7 FR5) — create or return the current
 * immutable upload attempt for ONE artifact and get a transport-explicit
 * presigned POST target. Body is attempt identity only ({ session_id,
 * artifact_id }); the `Idempotency-Key` header is REQUIRED (a replay before
 * expiry returns the same attempt). Returns { method:"POST", url, form_fields,
 * required_headers, expires_at, max_bytes, attempt_id, attempt_no, ... }. The
 * signed material exists only in this response — never persist/log it.
 */
export async function presignInput(jobId, { sessionId, artifactId }, idempotencyKey) {
  const h = headers();
  if (idempotencyKey) h["Idempotency-Key"] = idempotencyKey;
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:presign`, {
    method: "POST",
    headers: h,
    body: JSON.stringify({ session_id: sessionId, artifact_id: artifactId }),
  });
}

/**
 * Upload one file's bytes to its presigned object-store POST target (E02.7 FR18).
 *
 * Uses XMLHttpRequest — the documented exception to the "all network via fetch"
 * rule — for real upload-progress events (fetch cannot report request-body
 * progress). The presigned POST is a browser->object-store request:
 *   - build a FormData, append EVERY `form_fields` entry VERBATIM (order
 *     preserved) FIRST, then append the file LAST as fd.append("file", file,
 *     destinationName) — S3 POST policy requires the file field to be last;
 *   - send any `required_headers` verbatim, but NEVER an Authorization header
 *     (the POST policy is the auth) and NEVER set request Content-Type (the
 *     browser must set the multipart boundary itself).
 * Resolves on a 2xx (reporting fraction 1), rejects on a non-2xx / network error,
 * and is abortable via `opts.signal` (rejects with an AbortError).
 *
 * @param {object} presign  the PresignInputResponse
 * @param {File|Blob} file
 * @param {object} [opts]
 * @param {string} [opts.filename]  multipart filename (the destination_name)
 * @param {(fraction:number)=>void} [opts.onProgress]  0..1 best-effort
 * @param {AbortSignal} [opts.signal]
 */
export function postToObjectStore(presign, file, { filename, onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const abortErr = () => {
      const e = new Error("upload was cancelled");
      e.name = "AbortError";
      return e;
    };
    if (signal?.aborted) {
      reject(abortErr());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open(presign.method || "POST", presign.url);
    // Required signed headers verbatim (empty for a POST policy). Deliberately
    // NO Authorization header, and NO manual Content-Type (the browser sets the
    // multipart/form-data boundary).
    const requiredHeaders = presign.required_headers || {};
    for (const [k, v] of Object.entries(requiredHeaders)) xhr.setRequestHeader(k, v);
    if (typeof onProgress === "function" && xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (typeof onProgress === "function") onProgress(1);
        resolve({ status: xhr.status });
      } else {
        reject(new Error(`Upload failed: HTTP ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error("Upload failed: network error"));
    xhr.onabort = () => reject(abortErr());
    if (signal) {
      signal.addEventListener("abort", () => {
        try {
          xhr.abort();
        } catch {
          /* already settled */
        }
      });
    }
    const fd = new FormData();
    const fields = presign.form_fields || {};
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    // File LAST (S3 POST-policy requirement); the multipart filename is the
    // destination name, not the local filename.
    fd.append("file", file, filename || (file && file.name) || "file");
    xhr.send(fd);
  });
}

/**
 * POST /jobs/{jobId}/inputs:confirm (E02.7 FR6) — server-authoritative validation
 * of ONE attempt. Body is attempt identity only ({ session_id, attempt_id }); any
 * client size/hash/version field is a 422 (the server HEADs the object and records
 * observed size/checksum/version). Returns HTTP 200 EVEN ON A VALIDATION FAILURE —
 * the outcome rides in the body ({ artifact_status, failure_code, failure_reason,
 * observed_*, session_ready, ready_at }). A 410 means the upload target expired.
 */
export async function confirmInput(jobId, { sessionId, attemptId }) {
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:confirm`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId, attempt_id: attemptId }),
  });
}

/**
 * POST /jobs/{jobId}/inputs:finalize (E02.7 FR9) — the ONLY operation that routes
 * a ready managed-upload job. Body is session identity only ({ session_id }).
 * Returns the full JobResponse (status queued|vetoed, venue, non-empty Adam/Eve
 * reasoning once a decision exists) plus a sealed `upload_session`. Idempotent: a
 * replay returns the same result. A 429 is `queue_full` (retryable — the session
 * stays ready), NOT a rate limit.
 */
export async function finalizeInputs(jobId, sessionId) {
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:finalize`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId }),
  });
}

/**
 * POST /jobs/{jobId}/inputs:abort (E02.7 FR14) — idempotently abort an open
 * session and cancel the waiting job. Body is session identity only
 * ({ session_id }). Returns the AUTHORITATIVE terminal state ({ job_status:
 * "cancelled", input_failure_code:"input_upload_aborted", upload_session:{ status:
 * "aborted", ... } }); an unconfirmed network response is NOT proof of
 * cancellation, so the Console only shows "Cancelled" on this 200.
 */
export async function abortInputs(jobId, sessionId) {
  return fetchJsonCoded(`${BASE}/jobs/${encodeURIComponent(jobId)}/inputs:abort`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ session_id: sessionId }),
  });
}

/** GET /jobs - list recent jobs from the database. Returns { jobs, note? }. */
/**
 * GET /jobs — the tenant's job list.
 *
 * Two mutually independent filters, both optional:
 * - `status` is an EXACT match on `jobs.status` (the long-standing contract).
 * - `statusGroup` (OMEGA-829) is the coarse `pending` | `completed` split the Jobs
 *   screen's three-pill bar sends. `completed` covers every TERMINAL status, so a
 *   failed / vetoed / rejected / cancelled job is still reachable from a named filter;
 *   `pending` is its complement. Any other value is a 422 from the backend, never a
 *   silently unfiltered list.
 */
export async function listJobs(limit = 20, status, statusGroup) {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (status) qs.set("status", status);
  if (statusGroup) qs.set("status_group", statusGroup);
  return fetchJson(`${BASE}/jobs?${qs}`, { headers: headers() });
}

/**
 * GET /jobs/{id} - job detail with lifecycle, routing evidence (Adam/Eve
 * reasoning, profit, utility, rounds) and the latest execution outcome.
 */
export async function getJob(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}`, { headers: headers() });
}

/**
 * GET /jobs/{id}/outputs?limit=N — list a job's collected output artifacts
 * (E02.6 FR1). Returns { job_id, status, outputs[] } where each output is
 * { artifact_id, filename, uri, size_bytes?, checksum_sha256?, content_type?,
 * download_url?, expiry_secs? }. Server returns them in deterministic order
 * (uri ASC, artifact_id ASC) — consumers MUST NOT re-sort. A non-terminal job
 * returns 200 with an empty list so the panel can poll.
 */
export async function getJobOutputs(jobId, limit = 50) {
  const qs = new URLSearchParams({ limit: String(limit) });
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/outputs?${qs}`, { headers: headers() });
}

/**
 * GET /jobs/{id}/logs — resolve a job's log reference (E02.6 FR2). Returns
 * { job_id, status, logs_url?, source?: "job"|"latest_outcome", note? }. When
 * no URL exists the backend supplies a human-readable `note`.
 */
export async function getJobLogs(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/logs`, { headers: headers() });
}

/**
 * GET /jobs/{id}/log-events?cursor=&direction=&limit= — one page of a job's
 * structured log events, read live from the provider (E02.9). Returns
 * `{ job_id, status, events[{timestamp, ingestion_time?, message, stream,
 * event_id?}], cursors: {older?, newer?}, availability, partial, truncated }`.
 * `cursor` is opaque and application-owned — never a raw CloudWatch token; pass
 * it straight through from a previous response's `cursors.older`/`cursors.newer`.
 * `direction` defaults to "older" (null cursor + no direction = the newest
 * tail page). Throws on a non-2xx (429 upstream_throttled / 502
 * upstream_unavailable / 400 cursor_expired) — the other availability states
 * (`not_dispatched` / `stream_pending` / `available` / `unsupported_provider` /
 * `multi_run_unsupported` / `expired_or_missing`) are always 200 and carried in
 * the body's `availability` field.
 */
export async function getJobLogEvents(jobId, { cursor, direction, limit } = {}) {
  const qs = new URLSearchParams();
  if (cursor) qs.set("cursor", cursor);
  if (direction) qs.set("direction", direction);
  if (limit != null) qs.set("limit", String(limit));
  const suffix = qs.toString() ? `?${qs}` : "";
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}/log-events${suffix}`, { headers: headers() });
}

/**
 * Fetch the bytes of a presigned download URL for in-browser preview (F05 FR4).
 *
 * Like postToObjectStore, this is the documented exception to the "all
 * network via api.js helpers carry the Bearer token" rule: a presigned URL
 * carries its own signature in the query string, so attaching an Authorization
 * header would break the object-store signature. Returns the response body as
 * text; throws the real HTTP status on a non-2xx so the caller can surface it
 * (never fabricated content).
 */
export async function fetchPreview(downloadUrl) {
  // Intentionally NO Authorization header — the presigned URL is the auth.
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error(`Preview failed: HTTP ${res.status}`);
  }
  return res.text();
}

/**
 * POST /jobs/{id}:cancel — request cancellation of a non-terminal job (E04.6).
 *
 * The `:cancel` verb sits outside the encoded job id (matching
 * inputs:presign / inputs:confirm). Returns the 200 receipt
 * ({ job_id, status, cancelled, provider_run_id?, message }) on success. A 409
 * arrives on the NEW error envelope; readDetail surfaces its human `message`,
 * so the thrown Error message is the readable string.
 */
export async function cancelJob(jobId) {
  return fetchJson(`${BASE}/jobs/${encodeURIComponent(jobId)}:cancel`, {
    method: "POST",
    headers: headers(),
  });
}

/**
 * GET /tenants/me/usage — the authenticated tenant's current-period usage,
 * metered billing meters, active limits, and latest accrual (E03.3 + F06.1).
 * Figures are accrued from the usage_ledger at terminal state (billed), never a
 * submission-time projection. The accrued quantity is the compute the customer
 * DECLARED (`compute_units=compute_req`), not measured consumption.
 * Shape (F06.1 additive; `quota` is a transitional
 * alias the Console no longer reads):
 *   {
 *     tenant_id, period, period_start, currency,
 *     usage:  { compute_units, cost_actual, job_count },
 *     meters: {
 *       // Compute units are a BILLING/REPORTING meter, not quota headroom.
 *       // capped is derived from cap-row presence: false (default, every plan
 *       // tier) => limit/remaining null and must NOT render as "of X"; true =>
 *       // an explicit contractual cap, limit/remaining non-null.
 *       compute_units_period: { used, period, capped, limit, remaining },
 *     },
 *     limits: {
 *       // Enforceable active customer limits (admission-gated).
 *       concurrent_jobs: { limit, used, remaining },
 *       // Per-submission GPU ceiling — used/remaining are always null.
 *       max_gpu_count:   { limit, used: null, remaining: null },
 *     },
 *     quota: { … },              // transitional alias; do not use
 *     latest_accrual: { job_id, compute_units, cost_actual, accrued_at } | null
 *   }
 * Errors: 400 when the JWT has no tenant_id claim (object detail; readDetail
 * surfaces its message), 503 degraded (string detail), 401 on a bad token.
 */
export async function getMyUsage() {
  return fetchJson(`${BASE}/tenants/me/usage`, { headers: headers() });
}

/**
 * GET /tenants/me/billing — the authenticated tenant's billing status, currency,
 * plan, and card-on-file metadata (E03.7, F06-B FR4/FR6). Shape:
 * { tenant_id, status: "no_payment_method"|"ok"|"past_due", currency, plan,
 *   card: { brand, last4, exp_month, exp_year } | null }. `card` is null iff no
 * card is on file; the response_model guarantees no PAN/vendor refs on the wire.
 * Errors: 400 when the JWT has no tenant_id claim (object detail; readDetail
 * surfaces its message), 404 "billing account not found", 503 degraded
 * (string details), 401 on a bad token.
 */
export async function getMyBilling() {
  return fetchJson(`${BASE}/tenants/me/billing`, { headers: headers() });
}

// OMEGA-762 (2026-08-17) — `updateCard()` (PUT /tenants/me/billing/card) is REMOVED
// along with the CardPanel that was its only caller. The platform will not collect
// payment from a stored card (decision by Rick Zhou; Omega issues its own invoices,
// OMEGA-734 D1), so a client helper for putting one on file has nothing to serve.
//
// OMEGA-773 — two claims above went stale and are corrected here:
//   * "invoice + bank transfer" named a rail the owner REJECTED on 2026-08-18. No
//     rail is named now; E20 owns that decision and this client does not.
//   * "The backend route still exists ... retiring it is a separable backend change"
//     was true for one day. OMEGA-765 DELETED the route, along with UpdateCardRequest
//     and apply_card_update. There is no endpoint left to be unreachable from.

// ── E18 Credit System (F13 Console Credit screen) ──────────────────────────
// All four routes are gated by `credit_system_enabled` (default FALSE): with the
// gate off they return 404 with code "not_found" (mounted-but-disabled), so a
// caller MUST branch on `err.code === "not_found"` for the whole-screen gate-off
// state, NEVER on the 404 status (a redeem `redeem_code_not_found` is also a 404).

/**
 * GET /tenants/me/credit — the authenticated tenant's prepaid Credit balance
 * summary (E18 FR1, F13). Returns { tenant_id, currency, balance, available,
 * held, total_redeemed, total_consumed }; every money field is a 4-dp AUD string
 * (available = balance - held). A tenant with no credit row gets a ZEROED
 * summary (all "0.0000"), NOT a 404. Errors: 404 { code:"not_found" } when the
 * feature gate is off (the default), 400 missing tenant claim, 503 degraded, 401
 * on a bad token. The tenant is resolved from the JWT, never a query param.
 */
export async function getMyCredit() {
  return fetchJson(`${BASE}/tenants/me/credit`, { headers: headers() });
}

/**
 * GET /tenants/me/credit/transactions — the tenant's Credit ledger newest-first,
 * keyset-paginated (E18 FR2, F13). Options: `limit` (1..200, default server-side
 * 50), `cursor` (opaque keyset token from a prior `next_cursor` — pass it through
 * verbatim, never construct one), `type` (optional movement-type filter:
 * redeem|usage|refund|manual_adjustment|expired). Returns { tenant_id, currency,
 * items:[{ transaction_id, type, amount, balance_after, reference_id?, source,
 * description?, created_at }], next_cursor? } where `amount` is a SIGNED 4-dp AUD
 * string (+credit / -debit) and `next_cursor` is absent/null when no rows remain.
 * Errors as getMyCredit, plus 422 on a malformed cursor.
 */
export async function getMyCreditTransactions({ limit, cursor, type } = {}) {
  const qs = new URLSearchParams();
  if (limit != null) qs.set("limit", String(limit));
  if (cursor) qs.set("cursor", cursor);
  if (type) qs.set("type", type);
  const suffix = qs.toString() ? `?${qs}` : "";
  return fetchJson(`${BASE}/tenants/me/credit/transactions${suffix}`, { headers: headers() });
}

/**
 * POST /tenants/me/credit:redeem — redeem a code into the tenant's Credit balance
 * (E18 FR3/FR5, F13). Sends ONLY { code } and REQUIRES an `Idempotency-Key`
 * header (like submitJob's managed create): a replay with the SAME key + code
 * returns the original 201 verbatim (no double credit), the same key with a
 * DIFFERENT code -> 409 idempotency_conflict. Returns 201 { transaction_id, code,
 * amount, balance, available, currency, redeemed_at }. On a failure the thrown
 * Error carries the stable coded fields (err.code / err.status / err.request_id)
 * so RedeemForm maps each verdict via engine/creditErrors: 404
 * redeem_code_not_found, 409 redeem_code_disabled/_expired/_exhausted/
 * _already_redeemed, 403 redeem_code_not_eligible, 422 validation_error, 429
 * rate_limited, plus the gate-off 404 not_found / 503 degraded. The `:redeem`
 * colon-verb sits outside the path (matching :cancel / inputs:confirm).
 */
export async function redeemCode(code, idempotencyKey) {
  const h = headers();
  if (idempotencyKey) h["Idempotency-Key"] = idempotencyKey;
  return fetchJson(`${BASE}/tenants/me/credit:redeem`, {
    method: "POST",
    headers: h,
    body: JSON.stringify({ code }),
  });
}

/**
 * POST /tenants/me/credit:validate — read-only redeem-code validation for the
 * inline blur preview (E18 FR4, F13). Sends ONLY { code }; nothing is mutated and
 * no code is consumed. Returns 200 { valid: true, amount, currency } for a VALID
 * code. IMPORTANT: the backend NEVER returns { valid: false } — an invalid code
 * raises the SAME coded ApiError as :redeem (redeem_code_not_found / _expired /
 * ...), so a caller treats a THROWN error as "invalid" and maps err.code. The
 * redeem itself re-validates server-side, so a stale/optimistic preview is safe.
 */
export async function validateRedeemCode(code) {
  return fetchJson(`${BASE}/tenants/me/credit:validate`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ code }),
  });
}

/**
 * POST /tenants/me/credit:topup — start a customer-present card top-up (E21 FR2).
 *
 * Sends ONLY { amount_aud } as a JSON INTEGER (whole Australian dollars) and REQUIRES an
 * `Idempotency-Key`, same contract as redeemCode. Returns 201 { topup_id, status, amount,
 * currency, checkout_url, replayed }.
 *
 * `checkout_url` is where the browser must go, and the caller navigates the WHOLE PAGE there
 * rather than opening a tab (pop-up blockers eat a tab opened after an await). On an idempotent
 * replay `checkout_url` is null: the original Stripe Session lives at most 24 hours and handing
 * back a dead URL looks like a broken product, so the caller reads `status` instead.
 *
 * The amount is NOT validated here beyond what the caller does inline. The server is the
 * authority (`credit_topup.validate_amount`) and re-checks every submission, so a stale or
 * tampered client cannot widen the accepted range.
 *
 * Failure codes worth mapping (see engine/creditErrors): 422 validation_error with
 * details.field="amount_aud", 422 idempotency_key_required, 409 idempotency_conflict, 409
 * topup_blocked, 429 topup_limit_exceeded, 400 topup_origin_not_allowed, 502
 * payment_provider_unavailable, plus the gate-off 404 not_found and 503 degraded.
 */
export async function createCreditTopup(amountAud, idempotencyKey) {
  const h = headers();
  if (idempotencyKey) h["Idempotency-Key"] = idempotencyKey;
  return fetchJson(`${BASE}/tenants/me/credit:topup`, {
    method: "POST",
    headers: h,
    body: JSON.stringify({ amount_aud: amountAud }),
  });
}

/**
 * GET /tenants/me/credit/topups/{id} — the state of one top-up (E21 FR8).
 *
 * **The ONLY source for what the return page may say.** The browser can come back from Stripe
 * before the webhook has landed, so "the customer returned" and "the credit exists" are
 * different facts. Returns { topup_id, status, amount, currency, created_at, paid_at,
 * credit_transaction_id }.
 *
 * Read `status`, never the `?status=success` query parameter Stripe appended to the return URL:
 * that only proves the browser came back. `credited` (with a credit_transaction_id) is the only
 * value that means money became credit; `paid` means Stripe took it and the ledger has not
 * caught up yet.
 *
 * A topup_id belonging to another tenant is 404, not 403 — existence is not disclosed across a
 * tenant boundary.
 */
export async function getCreditTopup(topupId) {
  return fetchJson(`${BASE}/tenants/me/credit/topups/${encodeURIComponent(topupId)}`, {
    headers: headers(),
  });
}

/**
 * GET /users/me — the authenticated caller's profile + tenant context (E05.2,
 * F07 FR1). Shape: { user_id, username, full_name, email, role, created_at,
 * onboarded_at, tenant: { tenant_id, name, plan, status } | null }.
 * OMEGA-814 — `full_name` is what the Console DISPLAYS and `email` is the login
 * identity it shows on the Account screen; both are null for an account created
 * before migration 091, which has neither on file. For a newer account `username`
 * duplicates `email`, which is how the identity is stored rather than a licence to
 * read one for the other. `role` is the literal
 * "customer" (no role column yet); `tenant` is null for legacy users without a
 * tenant_id. `onboarded_at` (F12 FR1) is the ISO first-login completion stamp,
 * or null until the customer completes/skips onboarding — the Console gates the
 * first-run experience on it. The profile object passes through unchanged, so
 * the new field is surfaced without further parsing. Errors: 404 "user not
 * found", 503 degraded (string details surfaced via the thrown Error message),
 * 401 on a bad token.
 */
export async function getProfile() {
  return fetchJson(`${BASE}/users/me`, { headers: headers() });
}

/**
 * POST /users/me/onboarding:complete — mark the caller's first-login onboarding
 * complete (F12 FR1). Bearer auth, NO request body. Idempotent: the first call
 * stamps `onboarded_at = now()`; a subsequent call is a no-op returning the same
 * value. Returns { onboarded_at: "<iso>" }. The `:complete` colon-verb sits
 * outside the path (matching `:cancel` / `inputs:confirm`). Called on the
 * activation milestone (first job -> first decision) or an explicit skip.
 * Errors: 401 on a bad token, 404 "user not found", 503 degraded (string
 * details surfaced via the thrown Error message).
 */
export async function completeOnboarding() {
  return fetchJson(`${BASE}/users/me/onboarding:complete`, {
    method: "POST",
    headers: headers(),
  });
}

/**
 * POST /auth/password — change the authenticated caller's password (E05.1,
 * F07 FR2). Body is ONLY { current_password, new_password } — the confirmation
 * is a client-only guard and is NEVER sent. The username comes from the JWT sub
 * claim, never the body. Returns 204 with NO body, so we resolve on res.ok
 * WITHOUT calling res.json() (an empty body would throw). Errors: 401 "current
 * password is incorrect" (NOT a session expiry — its code is absent from
 * SESSION_EXPIRED_CODES, so it never logs the user out), 422 (new_password 8-128
 * chars; the envelope's human `message` surfaced by readDetail), 503. Neither
 * password is logged.
 *
 * F14 — a SUCCESSFUL change now ends the session it was made from. The backend
 * increments `users.credential_version` in the same transaction as the hash, so
 * every JWT minted before it (this one included) is revoked at once; on success
 * we therefore clear the stored token and fire PASSWORD_CHANGED_EVENT. This
 * supersedes the E05.1 sentence "existing JWTs remain valid after a change",
 * which was true when written and is now false.
 */
export async function changePassword(currentPassword, newPassword) {
  // 204 No Content — there is no body to parse.
  await fetchNoContent(`${BASE}/auth/password`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  // F14 FR11/FR12 — the change bumped users.credential_version server-side, so
  // the JWT that authorised THIS request is already dead. Clear it and announce
  // the revocation rather than waiting for the next authenticated call to come
  // back 401: that would leave the user sitting in a Console whose every request
  // is about to fail, which reads as a broken app rather than a deliberate
  // sign-out. Only fires on success — a 401 (wrong current password) throws
  // above and must leave the working session completely untouched.
  signalPasswordChanged();
  return undefined;
}

/**
 * GET /tenants/me/api-keys — the authenticated tenant's API keys (E05.3,
 * F07 FR3). Shape: { keys: [ { key_id, name, key_prefix, created_at,
 * last_used_at|null, revoked_at|null } ] }. Server-sorted newest-first — DO NOT
 * re-sort. Never carries a secret or hash; revoked keys stay listed (FR4).
 * Errors: 400 without a tenant claim (object detail; readDetail surfaces its
 * message), 503 degraded, 401 on a bad token.
 */
export async function listApiKeys() {
  return fetchJson(`${BASE}/tenants/me/api-keys`, { headers: headers() });
}

/**
 * POST /tenants/me/api-keys — create a new API key (E05.3, F07 FR3). Body is
 * { name }. Returns 201 with the key metadata PLUS `secret`
 * (omega_<prefix>_<tail>) which is returned EXACTLY ONCE and never retrievable
 * again — the caller must surface it once and then discard it (FR4: never
 * persist/log/re-display). Errors: 422 (name empty/>64), 400 without a tenant
 * claim, 503 degraded.
 */
export async function createApiKey(name) {
  return fetchJson(`${BASE}/tenants/me/api-keys`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ name }),
  });
}

/**
 * DELETE /tenants/me/api-keys/{key_id} — revoke a key (E05.3, F07 FR3). Returns
 * 204 with NO body, so we resolve on res.ok WITHOUT calling res.json(). The
 * key's row is kept for audit (revoked_at set) and still appears in the list.
 * An unknown OR cross-tenant key_id both return 404 (by design — never 403, to
 * avoid confirming another tenant's key exists). Errors: 404, 400, 503.
 */
export async function revokeApiKey(keyId) {
  // 204 No Content — there is no body to parse.
  return fetchNoContent(`${BASE}/tenants/me/api-keys/${encodeURIComponent(keyId)}`, {
    method: "DELETE",
    headers: headers(),
  });
}

/** GET /venues - get current venue state from Morpheus */
export async function listVenues() {
  return fetchJson(`${BASE}/venues`, { headers: headers() });
}

/** GET /health - backend health check */
export async function checkHealth() {
  const res = await fetch(`${BASE}/health`);
  return parseJsonOrThrow(res);
}

/** GET /metrics/scheduler - scheduling stats */
export async function getSchedulerMetrics() {
  return fetchJson(`${BASE}/metrics/scheduler`, { headers: headers() });
}

/** GET /metrics/detection - Dr Gow §5.4 service-performance windows */
export async function getDetectionMetrics({ filtered = true, sampleMinutes = 15, limit = 20 } = {}) {
  const qs = new URLSearchParams({
    filtered: String(filtered),
    sample_minutes: String(sampleMinutes),
    limit: String(limit),
  });
  return fetchJson(`${BASE}/metrics/detection?${qs}`, { headers: headers() });
}

/** GET /metrics/infra - Dr Gow §5.7 diagnostic samples */
export async function getInfraMetrics({ instanceId, sinceSeconds = 600, limit = 200 } = {}) {
  const qs = new URLSearchParams({
    since_seconds: String(sinceSeconds),
    limit: String(limit),
  });
  if (instanceId) qs.set("instance_id", instanceId);
  return fetchJson(`${BASE}/metrics/infra?${qs}`, { headers: headers() });
}

/** GET /metrics/alerts/active - Dr Gow §5.5 current alert state */
export async function getActiveAlerts() {
  return fetchJson(`${BASE}/metrics/alerts/active`, { headers: headers() });
}

/** POST /metrics/infra/ingest - host agent batch upload */
export async function ingestInfraSamples(samples) {
  return fetchJson(`${BASE}/metrics/infra/ingest`, {
    method: "POST",
    headers: headers(),
    body: JSON.stringify({ samples }),
  });
}
